import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import { UserNotificationsContext } from '../context/user-notifications/user-notifications'
import { UserDetailsContext } from '../context/user-details/user-details'
import NominalCodeMappingService from '../shared/services/nominal-code-mapping/nominal-code-mapping.service'
import { INominalSummaryResponse, INominalSummaryRow, NominalMappingSource } from '../shared/services/nominal-code-mapping/nominal-code-mapping.model'
import ConversionSourceService from '../shared/services/conversion-source/conversion-source.service'
import { IConversionSource } from '../shared/services/conversion-source/conversion-source.model'
import SpendBasedConversionService from '../shared/services/spend-based-conversion/spend-based-conversion.service'
import { ISpendBasedConversion } from '../shared/services/spend-based-conversion/spend-based-conversion.model'
import GroupService from '../shared/services/group/group.service'
import { IGroup } from '../shared/services/group/group.model'
import SubsidiaryService from '../shared/services/subsidiary/subsidiary.service'
import { ISubsidiary } from '../shared/services/subsidiary/subsidiary.model'
import { getPreferredSubsidiary, getSelectableSubsidiaries } from '../shared/utils/subsidiary-utils'
import { DEFRA_SPEND_CONVERSION_SOURCE_ID } from '../shared/constants/conversion-sources'
import {
  INominalRowEdit,
  NominalBulkAction,
  NominalStatusFilter,
  applyNominalEdit,
  buildNominalSavePlan,
  canAcceptSuggestion,
  hasNoNominalSpend,
  mappingToOutcomeValue,
  matchesNominalStatusFilter,
  normaliseNominalCode,
  pluralise,
  suggestionToOutcome,
} from '../shared/utils/nominal-code-utils'
import type { INominalOutcomeValue } from '../components/presentation-components/settings-components/data-import/nominal-mapping-outcome-picker'
import { INominalActivityOption, buildNominalActivityOptions } from '../components/presentation-components/settings-components/data-import/nominal-outcome-autocomplete'

// The state behind the Chart of Accounts table, shared by the settings page and the import step's
// inline table so the two cannot drift: the reference lists every row's outcome control needs, the
// unsaved edits, which saved rows are open for editing, the filters, suggestion acceptance, the
// bulk bar and the save.

export interface INominalReferenceData {
  sources: IConversionSource[]
  groups: IGroup[]
  // The organisation's default spend source. Null until the subsidiaries have loaded.
  defaultSourceId: string | null
  // True only when the organisation's subsidiaries use more than one spend source.
  showSourceChip: boolean
  activities: INominalActivityOption[]
  activitiesLoading: boolean
  // The activities as plain conversions, for matching suggestions and saved values against.
  activityConversions: ISpendBasedConversion[]
}

/**
 * The lists every row's outcome control shares, loaded once rather than per row: the spend
 * sources and groups for the Advanced drawer, and the default source's activities for the row
 * list. The default source is the default subsidiary's, else the platform default, as the
 * financial review resolves it.
 */
export const useNominalReferenceData = (): INominalReferenceData => {
  //Services
  const conversionSourceService = new ConversionSourceService()
  const spendBasedConversionService = new SpendBasedConversionService()
  const groupService = new GroupService()
  const subsidiaryService = SubsidiaryService.getInstance()
  //Context
  const { userDetails } = useContext(UserDetailsContext)
  //State
  const [sources, setSources] = useState<IConversionSource[]>([])
  const [groups, setGroups] = useState<IGroup[]>([])
  const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[] | null>(null)
  const [activities, setActivities] = useState<INominalActivityOption[]>([])
  const [activitiesLoading, setActivitiesLoading] = useState<boolean>(true)

  useEffect(() => {
    // Every row's control reads these lists, so anything but a list is treated as an empty one.
    conversionSourceService.getAll(true, 'spendBasedConversion')
      .then(response => setSources(Array.isArray(response) ? response : []))
      .catch(err => console.log(err))
    groupService.getGroups()
      .then(response => setGroups(Array.isArray(response) ? response : []))
      .catch(err => console.log(err))
    subsidiaryService.getAll()
      .then(response => setSubsidiaries(Array.isArray(response) ? response : []))
      .catch(err => {
        console.log(err)
        setSubsidiaries([])
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const defaultSourceId = useMemo(() => {
    if (subsidiaries === null) return null
    return getPreferredSubsidiary(subsidiaries, userDetails)?.defaultSpendBasedConversionSourceId || DEFRA_SPEND_CONVERSION_SOURCE_ID
  }, [subsidiaries, userDetails])

  //Only an organisation whose subsidiaries use different spend sources ever sees the word "source".
  const showSourceChip = useMemo(() => new Set(getSelectableSubsidiaries(subsidiaries)
    .map(subsidiary => subsidiary.defaultSpendBasedConversionSourceId)
    .filter(Boolean)).size > 1, [subsidiaries])

  useEffect(() => {
    if (!defaultSourceId) return
    setActivitiesLoading(true)
    spendBasedConversionService.getSpendBasedConversionsForConversionSource(defaultSourceId, moment().year().toString(), true, true)
      .then((response: any) => {
        const rows: ISpendBasedConversion[] = Array.isArray(response) ? response : (response?.rows || [])
        setActivities(buildNominalActivityOptions(rows))
        setActivitiesLoading(false)
      })
      .catch(err => {
        console.log(err)
        setActivities([])
        setActivitiesLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSourceId])

  const activityConversions = useMemo(() => activities.map(option => option.conversion), [activities])

  return { sources, groups, defaultSourceId, showSourceChip, activities, activitiesLoading, activityConversions }
}

/** The activity chosen in the bulk bar's "Set activity" list. */
export interface INominalBulkActivity {
  value: INominalOutcomeValue
  conversion: ISpendBasedConversion | null
}

export interface INominalSaveOptions {
  // Which surface the batch came from, for the telemetry.
  source: NominalMappingSource
  provider?: string
  // Work that depends on the mappings being written, run before the edits clear: the import step
  // re-runs its upload's validation here. A failure is reported but does not undo the save.
  afterWrite?: () => Promise<unknown>
  // Said when afterWrite fails, after the count of codes saved.
  afterWriteFailedMessage?: string
  // The success message for the codes upserted. Defaults to what the re-apply did to lines in
  // review, else the count.
  describeSaved?: (savedCount: number, reapplyMessage: string | null) => string
  // After a save that reached the server, whatever happened to each code: reload the rows.
  onSaved?: () => void
}

export interface IUseNominalCodeMappingOptions {
  // The loaded summary. Null while the first load is in flight.
  summary: INominalSummaryResponse | null
  // The default source's activities, from useNominalReferenceData.
  activityConversions: ISpendBasedConversion[]
  // Whether codes never seen on a line are shown to begin with.
  defaultShowNoSpend: boolean
  // Changing this (or defaultShowNoSpend) puts the no-spend switch back to its default: the page
  // passes its ledger and period, so reading a ledger hides the never-used accounts again.
  viewKey?: string
  // False on the import step: "Clear" then only undoes unsaved changes and never marks a saved
  // mapping for deletion, because saved mappings are managed on the Chart of Accounts page.
  allowClearSaved?: boolean
}

export const useNominalCodeMapping = ({
  summary,
  activityConversions,
  defaultShowNoSpend,
  viewKey = '',
  allowClearSaved = true,
}: IUseNominalCodeMappingOptions) => {
  //Services
  const nominalCodeMappingService = new NominalCodeMappingService()
  //Context
  const userNotificationsContext = useContext(UserNotificationsContext)
  //Unsaved work
  const [edits, setEdits] = useState<Record<string, INominalRowEdit>>({})
  const [isSaving, setIsSaving] = useState<boolean>(false)
  //Saved rows put back into edit mode ("Change outcome"), keyed by normalised code. A saved row
  //otherwise shows its outcome as text; one with an unsaved edit shows the picker regardless.
  const [editingKeys, setEditingKeys] = useState<Set<string>>(() => new Set())
  //View
  const [statusFilter, setStatusFilter] = useState<NominalStatusFilter>('needsDecision')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [showNoSpend, setShowNoSpend] = useState<boolean>(defaultShowNoSpend)
  const [selected, setSelected] = useState<string[]>([])
  const [bulkAction, setBulkAction] = useState<NominalBulkAction>('exclude')
  const [bulkActivity, setBulkActivity] = useState<INominalBulkActivity | null>(null)
  const [advancedKey, setAdvancedKey] = useState<string | null>(null)

  const hasUnsaved = Object.keys(edits).length > 0

  //The user's own choices for this view. The automatic defaults below never override them.
  const noSpendTouchedRef = useRef<boolean>(false)
  const filterTouchedRef = useRef<boolean>(false)
  useEffect(() => {
    noSpendTouchedRef.current = false
    filterTouchedRef.current = false
    setShowNoSpend(defaultShowNoSpend)
  }, [defaultShowNoSpend, viewKey])

  const setShowNoSpendByUser = useCallback((value: boolean) => {
    noSpendTouchedRef.current = true
    setShowNoSpend(value)
  }, [])

  const setStatusFilterByUser = useCallback((value: NominalStatusFilter) => {
    filterTouchedRef.current = true
    setStatusFilter(value)
  }, [])

  //Derived rows
  const codes = useMemo(() => summary?.codes || [], [summary])
  const rowsByKey = useMemo(() => new Map(codes.map(row => [normaliseNominalCode(row.nominalCode), row])), [codes])

  //The row callbacks are handed to every row's memoised control, so they must stay stable; they
  //read the latest rows and activities through refs.
  const rowsByKeyRef = useRef(rowsByKey)
  rowsByKeyRef.current = rowsByKey
  const activityConversionsRef = useRef(activityConversions)
  activityConversionsRef.current = activityConversions

  //A scope narrows only the totals on the backend: every saved mapping and spreadsheet code is
  //still returned. The rows are narrowed to the scope here, so "the codes on lines in review for
  //this period" and "the codes in this upload" are true.
  const scope = summary?.scope || 'all'
  const scopedRows = useMemo(() => {
    if (scope === 'review') return codes.filter(row => (row.origins || []).includes('lines'))
    if (scope === 'upload') return codes.filter(row => (row.origins || []).includes('upload'))
    return codes
  }, [codes, scope])

  const noSpendCount = useMemo(() => scopedRows.filter(hasNoNominalSpend).length, [scopedRows])

  //A ledger brings every account in its chart, most never posted to, so they start hidden. But
  //hiding them must never leave the page empty: when every code with spend already has an
  //outcome, the codes with no spend are the work, and they are shown without being asked for.
  useEffect(() => {
    if (defaultShowNoSpend || noSpendTouchedRef.current || !summary) return
    const openWithSpend = scopedRows.some(row => !hasNoNominalSpend(row) && matchesNominalStatusFilter(row, 'needsDecision'))
    if (!openWithSpend && noSpendCount > 0) setShowNoSpend(true)
  }, [defaultShowNoSpend, summary, scopedRows, noSpendCount])

  //Whether any code in scope still carries a suggestion nobody has acted on. When none does, the
  //"Suggested" filter and "Accept N suggestions" have nothing to offer and are not shown.
  const hasOpenSuggestions = useMemo(() => scopedRows.some(row => !row.mapping && !!row.suggestion), [scopedRows])

  //Search and the no-spend switch first, so the filter counts describe what each filter would show.
  const searchedRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    return scopedRows.filter(row => {
      if (!showNoSpend && hasNoNominalSpend(row)) return false
      if (!query) return true
      return row.nominalCode.toLowerCase().includes(query)
        || (row.nominalDescription || '').toLowerCase().includes(query)
    })
  }, [scopedRows, searchQuery, showNoSpend])

  const filterCounts = useMemo((): Record<NominalStatusFilter, number> => ({
    needsDecision: searchedRows.filter(row => matchesNominalStatusFilter(row, 'needsDecision')).length,
    suggested: searchedRows.filter(row => matchesNominalStatusFilter(row, 'suggested')).length,
    mapped: searchedRows.filter(row => matchesNominalStatusFilter(row, 'mapped')).length,
    all: searchedRows.length,
  }), [searchedRows])

  //The page opens on the work queue when there is work, and on the full list when there is not.
  //An organisation whose every code already has an outcome should see its codes, not an empty
  //filter telling it so. Applies until the user picks a filter for this view.
  useEffect(() => {
    if (!summary || filterTouchedRef.current) return
    setStatusFilter(filterCounts.needsDecision > 0 ? 'needsDecision' : 'all')
  }, [summary, filterCounts])

  //The "Suggested" filter is not offered once nothing is suggested, so a view left on it moves on.
  useEffect(() => {
    if (!summary || hasOpenSuggestions || statusFilter !== 'suggested') return
    setStatusFilter(filterCounts.needsDecision > 0 ? 'needsDecision' : 'all')
  }, [summary, hasOpenSuggestions, statusFilter, filterCounts])

  const filteredRows = useMemo(
    () => searchedRows.filter(row => matchesNominalStatusFilter(row, statusFilter)),
    [searchedRows, statusFilter],
  )

  //Suggestions in the current view that are still open and can be accepted as they stand.
  const acceptableSuggestionKeys = useMemo(() => filteredRows
    .filter(row => !row.mapping && row.suggestion && !edits[normaliseNominalCode(row.nominalCode)]
      && canAcceptSuggestion(row.suggestion, activityConversions))
    .map(row => normaliseNominalCode(row.nominalCode)), [filteredRows, edits, activityConversions])

  //A tick on a row that has since been filtered out of view is not acted on.
  const visibleKeys = useMemo(() => new Set(filteredRows.map(row => normaliseNominalCode(row.nominalCode))), [filteredRows])
  const selectedVisible = useMemo(() => selected.filter(key => visibleKeys.has(key)), [selected, visibleKeys])

  useEffect(() => {
    setSelected([])
  }, [statusFilter, searchQuery, showNoSpend, summary])

  //Edit mode is for the rows in front of the user: a new filter, search or ledger closes it.
  useEffect(() => {
    setEditingKeys(prev => (prev.size === 0 ? prev : new Set()))
  }, [statusFilter, searchQuery, showNoSpend, viewKey])

  const footerCounts = useMemo(() => {
    const values = Object.values(edits)
    return {
      total: values.length,
      categorised: values.filter(edit => edit.value?.mappingType === 'spend' && edit.value.spendBasedConversionId).length,
      excluded: values.filter(edit => edit.value?.mappingType === 'exclude').length,
      notImported: values.filter(edit => edit.value?.mappingType === 'private').length,
      cleared: values.filter(edit => !edit.value).length,
    }
  }, [edits])

  //Handlers
  const changeHandler = useCallback((key: string, next: INominalOutcomeValue, conversion: ISpendBasedConversion | null) => {
    setEdits(prev => applyNominalEdit(prev, key, rowsByKeyRef.current.get(key), { value: next, conversion }))
  }, [])

  // Accepting copies the suggestion's resolved outcome (activity, source and any group) into the
  // row and remembers the suggestion, so the save records what was suggested against what was kept.
  const acceptSuggestionHandler = useCallback((key: string) => {
    const row = rowsByKeyRef.current.get(key)
    if (!row?.suggestion || row.mapping) return
    const accepted = suggestionToOutcome(row.suggestion, activityConversionsRef.current)
    if (!accepted) return
    setEdits(prev => applyNominalEdit(prev, key, row, { ...accepted, suggestion: row.suggestion }))
  }, [])

  const acceptAllSuggestionsHandler = () => {
    setEdits(prev => acceptableSuggestionKeys.reduce((acc, key) => {
      const row = rowsByKey.get(key)
      if (!row?.suggestion || acc[key]) return acc
      const accepted = suggestionToOutcome(row.suggestion, activityConversions)
      return accepted ? applyNominalEdit(acc, key, row, { ...accepted, suggestion: row.suggestion }) : acc
    }, prev))
  }

  /** Put a saved row back into edit mode, so its picker shows with the saved outcome in it. */
  const startEditingHandler = useCallback((key: string) => {
    setEditingKeys(prev => {
      if (prev.has(key)) return prev
      const next = new Set(prev)
      next.add(key)
      return next
    })
  }, [])

  /** Drop the unsaved edits for these codes, putting each row back as it was loaded. */
  const removeEdits = useCallback((keys: string[]) => {
    setEdits(prev => {
      const remaining = { ...prev }
      keys.forEach(key => delete remaining[key])
      return remaining
    })
    setEditingKeys(prev => {
      if (!keys.some(key => prev.has(key))) return prev
      const next = new Set(prev)
      keys.forEach(key => next.delete(key))
      return next
    })
  }, [])

  const undoChangeHandler = useCallback((key: string) => removeEdits([key]), [removeEdits])

  const openAdvancedHandler = useCallback((key: string) => setAdvancedKey(key), [])
  const closeAdvancedHandler = useCallback(() => setAdvancedKey(null), [])

  const applyBulkHandler = async () => {
    const keys = selectedVisible
    if (keys.length === 0) return
    if (bulkAction === 'activity') {
      if (!bulkActivity) return
      setEdits(prev => keys.reduce((acc, key) => {
        const row = rowsByKey.get(key)
        const current = acc[key] ? acc[key].value : mappingToOutcomeValue(row?.mapping)
        return applyNominalEdit(acc, key, row, {
          value: {
            mappingType: 'spend',
            spendBasedConversionId: bulkActivity.value.spendBasedConversionId ?? null,
            conversionSourceId: bulkActivity.value.conversionSourceId ?? null,
            excludeReason: null,
            groupId: current?.mappingType === 'spend' ? current.groupId ?? null : null,
          },
          conversion: bulkActivity.conversion,
        })
      }, prev))
    } else if (bulkAction === 'private') {
      setEdits(prev => keys.reduce((acc, key) => applyNominalEdit(acc, key, rowsByKey.get(key), {
        value: { mappingType: 'private', spendBasedConversionId: null, conversionSourceId: null, excludeReason: null, groupId: null },
        conversion: null,
      }), prev))
    } else if (bulkAction === 'exclude') {
      setEdits(prev => keys.reduce((acc, key) => {
        const row = rowsByKey.get(key)
        const current = acc[key] ? acc[key].value : mappingToOutcomeValue(row?.mapping)
        // Keep a reason already given, else the suggestion's, so a bulk exclude still says why.
        const reason = current?.mappingType === 'exclude'
          ? current.excludeReason ?? null
          : (row?.suggestion?.mappingType === 'exclude' ? row.suggestion.reason : null)
        return applyNominalEdit(acc, key, row, {
          value: { mappingType: 'exclude', spendBasedConversionId: null, conversionSourceId: null, excludeReason: reason, groupId: null },
          conversion: null,
        })
      }, prev))
    } else if (!allowClearSaved) {
      removeEdits(keys)
    } else {
      const savedCount = keys.filter(key => rowsByKey.get(key)?.mapping).length
      if (savedCount > 0) {
        const confirmed = await userNotificationsContext.openDialog({
          active: true,
          title: 'Clear saved outcomes?',
          message: `${pluralise(savedCount, 'selected code has', 'selected codes have')} a saved outcome. Clearing removes it when you save, and the spend on ${savedCount === 1 ? 'that code' : 'those codes'} goes back to classifying by supplier.`,
          buttonCopy: 'Clear',
          cancelButtonCopy: 'Cancel',
          hideCancelButton: false,
          have_form_input: false,
        })
        if (confirmed !== true) return
      }
      setEdits(prev => {
        const next = { ...prev }
        keys.forEach(key => {
          const row = rowsByKey.get(key)
          if (row?.mapping) {
            next[key] = {
              nominalCode: row.mapping.nominalCode || row.nominalCode,
              nominalDescription: row.mapping.nominalDescription ?? row.nominalDescription ?? null,
              mappingId: row.mapping.id,
              value: null,
              conversion: null,
              suggestion: null,
            }
          } else {
            delete next[key]
          }
        })
        return next
      })
    }
    setSelected([])
  }

  // The same "Discard your changes?" DialogWrapper asks, for leaving the page or discarding outright.
  const confirmDiscardChanges = async (reason: 'leave' | 'discard'): Promise<boolean> => {
    if (!hasUnsaved) return true
    const them = footerCounts.total === 1 ? 'it' : 'them'
    const confirmed = await userNotificationsContext.openDialog({
      active: true,
      title: 'Discard your changes?',
      message: reason === 'leave'
        ? `You have ${pluralise(footerCounts.total, 'unsaved change', 'unsaved changes')} on this page. Leaving now will discard ${them}.`
        : `Every code you have changed goes back to how it was saved. ${pluralise(footerCounts.total, 'unsaved change', 'unsaved changes')} will be lost.`,
      buttonCopy: 'Discard',
      cancelButtonCopy: 'Keep editing',
      hideCancelButton: false,
      have_form_input: false,
    })
    return confirmed === true
  }

  const discardHandler = async () => {
    if (!(await confirmDiscardChanges('discard'))) return
    setEdits({})
    setEditingKeys(new Set())
    setSelected([])
  }

  /**
   * Save every complete edit: upserts in one bulk call, each carrying the suggestion it was
   * accepted from, then cleared mappings deleted one at a time. Only edits that saved, and that
   * were not changed again while saving, are cleared; the rest stay marked Unsaved.
   */
  const save = async (options: INominalSaveOptions): Promise<void> => {
    if (isSaving) return
    const snapshot = edits
    const { payload, payloadKeys, deletions, incomplete } = buildNominalSavePlan(snapshot)
    if (payload.length === 0 && deletions.length === 0) {
      userNotificationsContext.openSnackbar({ open: true, message: 'Choose an activity for each categorised code before saving', backgroundColor: 'text.primary' })
      return
    }
    // Marking a code "Don't import" deletes the lines already brought in on it, which cannot be
    // undone, so it is confirmed first. Only codes newly marked: re-saving one already private
    // deletes nothing new.
    const newlyPrivate = payloadKeys.filter((key, index) => payload[index]?.mappingType === 'private'
      && rowsByKey.get(key)?.mapping?.mappingType !== 'private')
    if (newlyPrivate.length > 0) {
      const confirmed = await userNotificationsContext.openDialog({
        active: true,
        title: `Stop importing ${pluralise(newlyPrivate.length, 'code', 'codes')}?`,
        message: `Lines on ${newlyPrivate.length === 1 ? 'this code' : 'these codes'} will no longer be brought in from your ledger, and any already brought in will be deleted when you save, unless they have been committed. Deleted lines cannot be restored.`,
        buttonCopy: 'Save and delete',
        cancelButtonCopy: 'Keep editing',
        hideCancelButton: false,
        have_form_input: false,
      })
      if (confirmed !== true) return
    }
    setIsSaving(true)
    const savedKeys = new Set<string>()
    let reapplyMessage: string | null = null
    let failed = 0
    try {
      if (payload.length > 0) {
        const response = await nominalCodeMappingService.bulkUpsert(payload, { source: options.source, provider: options.provider })
        const results = (Array.isArray(response?.results) ? response.results : []) as { nominalCode?: string, error?: string }[]
        const failedCodes = new Set(results.filter(result => result && result.error).map(result => normaliseNominalCode(result.nominalCode)))
        payloadKeys.forEach(key => {
          if (failedCodes.has(key)) failed += 1
          else savedKeys.add(key)
        })
        reapplyMessage = response?.reapplyMessage || null
      }
      //Deletes run one at a time, like the bulk writes, and one failing does not stop the rest.
      let deleted = 0
      for (const deletion of deletions) {
        try {
          await nominalCodeMappingService.delete(deletion.id)
          savedKeys.add(deletion.key)
          deleted += 1
        } catch (err) {
          console.log(err)
          failed += 1
        }
      }
      let afterWriteFailed = false
      if (options.afterWrite && savedKeys.size > 0) {
        try {
          await options.afterWrite()
        } catch (err) {
          console.log(err)
          afterWriteFailed = true
        }
      }
      setEdits(prev => {
        const remaining = { ...prev }
        savedKeys.forEach(key => {
          if (remaining[key] === snapshot[key]) delete remaining[key]
        })
        return remaining
      })
      //Saved rows go back to text. A row whose change could not be saved keeps its edit, and so
      //its picker.
      setEditingKeys(new Set())
      const upserted = savedKeys.size - deleted
      const parts: string[] = []
      if (upserted > 0) {
        if (afterWriteFailed) parts.push(`${pluralise(upserted, 'code', 'codes')} saved.`)
        else parts.push(options.describeSaved
          ? options.describeSaved(upserted, reapplyMessage)
          : (reapplyMessage || `${pluralise(upserted, 'code', 'codes')} saved.`))
      }
      if (deleted > 0) parts.push(`${pluralise(deleted, 'outcome', 'outcomes')} cleared.`)
      if (failed > 0) parts.push(`${pluralise(failed, 'change', 'changes')} could not be saved and ${failed === 1 ? 'is' : 'are'} still marked Unsaved.`)
      if (incomplete > 0) parts.push(`${pluralise(incomplete, 'code has', 'codes have')} no activity chosen yet and ${incomplete === 1 ? 'was' : 'were'} not saved.`)
      if (afterWriteFailed && options.afterWriteFailedMessage) parts.push(options.afterWriteFailedMessage)
      userNotificationsContext.openSnackbar({
        open: true,
        message: parts.join(' '),
        ...(failed > 0 || afterWriteFailed ? { backgroundColor: 'text.primary' } : {}),
      })
      options.onSaved?.()
    } catch (err: any) {
      console.log(err)
      userNotificationsContext.openSnackbar({ open: true, message: err?.response?.data?.message || 'Your changes could not be saved. Please try again.', backgroundColor: 'text.primary' })
    } finally {
      setIsSaving(false)
    }
  }

  const advancedRow: INominalSummaryRow | undefined = advancedKey ? rowsByKey.get(advancedKey) : undefined
  const advancedEdit: INominalRowEdit | undefined = advancedKey ? edits[advancedKey] : undefined

  return {
    // Rows
    codes,
    rowsByKey,
    scope,
    scopedRows,
    noSpendCount,
    filteredRows,
    filterCounts,
    // View
    statusFilter,
    setStatusFilter: setStatusFilterByUser,
    searchQuery,
    setSearchQuery,
    showNoSpend,
    setShowNoSpend: setShowNoSpendByUser,
    selected,
    setSelected,
    selectedVisible,
    // Edits
    edits,
    hasUnsaved,
    footerCounts,
    changeHandler,
    undoChangeHandler,
    removeEdits,
    confirmDiscardChanges,
    discardHandler,
    // Edit mode for saved rows
    editingKeys,
    startEditingHandler,
    // Suggestions
    hasOpenSuggestions,
    acceptableSuggestionKeys,
    acceptSuggestionHandler,
    acceptAllSuggestionsHandler,
    // Bulk bar
    allowClearSaved,
    bulkAction,
    setBulkAction,
    bulkActivity,
    setBulkActivity,
    applyBulkHandler,
    // Advanced drawer
    advancedKey,
    advancedRow,
    advancedEdit,
    openAdvancedHandler,
    closeAdvancedHandler,
    // Save
    isSaving,
    save,
  }
}

export type INominalCodeMappingState = ReturnType<typeof useNominalCodeMapping>
